import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';

/**
 * Visitor Insights (`/api/admin/analytics/visitors/*`). Every source answers in
 * the same shape, so the page renders FeelyFeely, Plausible and (later) Google
 * Analytics with one set of panels.
 */
export type VisitorProvider = {
  key: string;
  label: string;
  connected: boolean;
  dimensions: string[];
  journeys: boolean;
  note?: string;
};

export type VisitorMetrics = {
  visitors: number;
  visits: number;
  pageviews: number;
  views_per_visit: number;
  /** Percent, 0-100. */
  bounce_rate: number;
  /** Seconds. */
  visit_duration: number;
};
export type VisitorMetric = keyof VisitorMetrics;

export type VisitorSummary = {
  provider: string;
  from: string;
  to: string;
  interval: 'day' | 'hour';
  totals: VisitorMetrics;
  previous: VisitorMetrics;
  series: ({ t: string } & VisitorMetrics)[];
  realtime: number | null;
};

export type VisitorRow = {
  name: string;
  /** What to send back as the filter for this row. */
  value: string;
  code?: string;
  visitors: number;
  visits?: number;
  pageviews?: number;
};

export type VisitorQuery = {
  provider: string;
  from: string;
  to: string;
  filters: Record<string, string>;
};

const BASE = '/api/admin/analytics/visitors';
const STALE = 60_000;

/** ff-api wraps payloads as `{ status, data }`. */
const unwrap = <T,>(raw: any): T =>
  raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw ? raw.data : raw;

export const visitorParams = (q: VisitorQuery, extra: Record<string, string> = {}) =>
  new URLSearchParams({
    provider: q.provider,
    from: q.from,
    to: q.to,
    ...(Object.keys(q.filters).length ? { filters: JSON.stringify(q.filters) } : {}),
    ...extra,
  }).toString();

export const visitorBreakdownPath = `${BASE}/breakdown`;

const useGet = () => {
  const request = useClientStore((s) => s.request);
  return async <T,>(path: string): Promise<T> => unwrap<T>(await request<any>(path));
};

export const useVisitorProviders = () => {
  const get = useGet();
  return useQuery({
    queryKey: ['visitor-insights', 'providers'],
    queryFn: async () => (await get<{ providers: VisitorProvider[] }>(`${BASE}/providers`)).providers,
    staleTime: 10 * STALE,
  });
};

export const useVisitorSummary = (q: VisitorQuery, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: ['visitor-insights', 'summary', q],
    queryFn: () => get<VisitorSummary>(`${BASE}/summary?${visitorParams(q)}`),
    enabled,
    staleTime: STALE,
    refetchInterval: STALE,
    placeholderData: keepPreviousData,
    retry: false,
  });
};

export const useVisitorBreakdown = (q: VisitorQuery, dimension: string, limit = 10, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: ['visitor-insights', 'breakdown', q, dimension, limit],
    queryFn: async () =>
      (await get<{ rows: VisitorRow[] }>(
        `${BASE}/breakdown?${visitorParams(q, { dimension, limit: String(limit) })}`,
      )).rows,
    enabled,
    staleTime: STALE,
    placeholderData: keepPreviousData,
    retry: false,
  });
};

export const useVisitorJourney = (q: VisitorQuery, steps: string[]) => {
  const get = useGet();
  return useQuery({
    queryKey: ['visitor-insights', 'journey', q, steps],
    queryFn: async () =>
      (await get<{ rows: VisitorRow[] }>(
        `${BASE}/journey?${visitorParams(q, { steps: JSON.stringify(steps) })}`,
      )).rows,
    enabled: steps.length > 0,
    staleTime: STALE,
    retry: false,
  });
};
